import {Injectable} from '@angular/core';
import {Observable, of} from 'rxjs';
import {HttpClient, HttpParams} from '@angular/common/http';
import {map} from 'rxjs/operators';
import {environment} from '../../../environments/environment';
import {Form} from "@angular/forms";

const {API_DEVELOPER_URL, API_PUBLIC_URL, API_URL, API_AI_KEY} = environment;

export interface APIResponse {
  code: number;
  data?: any;
}

export interface ProjectDescription {
  project_name: string;
  address: string;
  project_type: string;
  project_status: string;
  total_unit?: number;
  start_date?: string;
  end_date?: string;
  min_price?: number;
  max_price?: number;
  min_bedrooms?: string;
  max_bedrooms?: string;
  min_bathrooms?: number;
  max_bathrooms?: number;
  min_car_spaces?: number;
  max_car_spaces?: number;
  // indoor_features?: number[];
  // outdoor_features?: number[];
  // public_facility?: number[];
  project_features?: number[];
}

@Injectable({
  providedIn: 'root'
})
export class HttpService {
  public env: any;
  apiUrl: string;
  projectUrl: string;
  systemUrl: string;
  messageUrl: string;
  agentUrl: string;
  consumerUrl: string;

  constructor(private http: HttpClient) {
    this.apiUrl = `${API_DEVELOPER_URL}/yp-api/customer/api`;
    this.projectUrl = `${API_DEVELOPER_URL}/yp-api/project-api/api`;
    this.systemUrl = `${API_DEVELOPER_URL}/yp-api/system/api`;
    this.messageUrl = `${API_DEVELOPER_URL}/yp-api/message/api`;
    this.agentUrl = `${API_DEVELOPER_URL}/yp-api/agent/api`;
    this.consumerUrl = `${API_DEVELOPER_URL}/yp-api/consumer/api`;
  }

  /**
   * @function getCryptoKey
   * @description The function provides the secret key to decrypt http response
   * @param None
   * @return {Observable}<String>
   */
  getCryptoKey(): Observable<string> {
    if (this.env) {
      return of(this.env.cryptoKey);
    } else {
      return this.getEnvironments().pipe(
        map((res: any) => {
          if (res.code === 1) {
            this.env = res.data[0];

            const cryptoKeyFromServer = res.data[0].cryptoKey;
            const realKey = cryptoKeyFromServer.substring(8, cryptoKeyFromServer.length - 8);
            // console.log(realKey);
            this.env.cryptoKey = realKey;
            return realKey;
          }
        })
      );
    }
  }

  /**
   * @function getDevRoles
   * @description The function find all the developer roles
   * @param None
   * @return {Observable}
   */
  // 返回跟dev有关的角色
  getDevRoles() {
    return this.http.get(`${this.systemUrl}/auth/roles/by-dev`);
  }

  getDraftProjectList() {
    return this.http.get(`${this.projectUrl}/anon/projects/search?projectStatus=5`);
  }

  /**
   * @function createUserByDevAdmin
   * @description The function create a user by dev-admin
   * @param {FormData} fd
   * @return {Observable}
   */
  // dev-admin创建用户
  createUserByDevAdmin(fd) {
    return this.http.post(`${this.systemUrl}/auth/users/create/by-dev`, fd);
  }

  /**
   * @function deleteStaff
   * @description The function allow dev-admin to delete staff by staff id
   * @param {number} staffId
   * @return {Observable}
   */
  // dev-admin删除用户
  deleteStaff(staffId) {
    return this.http.delete(`${this.systemUrl}/auth/users/by-auth/${staffId}`);
  }

  /**
   * @function getEnvironments
   * @description The function provide environments context such as appVersion, cryptoKey(AES), webVersion, notice[upgrade website notice]
   * @param None
   * @return {Observable}
   */
  getEnvironments() {
    return this.http.get(`${API_PUBLIC_URL}/yp-api/system/api/anon/environments`);
  }

  /**
   * @function getAllUsersByCompany
   * @description The function provide users list based on current user's company. Only can be used by dev-admin, agent admin
   * @param None
   * @return {Observable<APIResponse>}
   */
  // 根据当前登录的用户来查询公司下所有的用户, 无权限返回401（Unauthorized）。只有dev-admin，agent-admin可以调用
  getAllUsersByCompany(page?, pageSize?): Observable<APIResponse> {
    return this.http.get<APIResponse>(`${this.systemUrl}/auth/users/company/by-auth`);
  }

  /**
   * @function getCompanyById
   * @description The function is used to check company detail based on user's company id
   * @param {number} companyId
   * @return {Observable}
   */
  // 根据user的company id查找company
  getCompanyById(companyId) {
    return this.http.get(`${this.systemUrl}/auth/companies/${companyId}`, {withCredentials: false});
  }

  /**
   * @function getProjectDetailByProjectId
   * @description The function provide project detail based on project id. Only agent-admin, agent-manager, agent-employee, agent-affiliate can use
   * @param {string} projectId (encrypted)
   * @return {Observable}
   */
  // dev根据项目ID查找项目的详细信息
  getProjectDetailByProjectId(projectId, page?, pageSize?) {
    return this.http.get(`${this.projectUrl}/auth/developer/projects/${projectId}`);
  }

  /**
   * @function getAllSimpleProjectsByUser
   * @description The function provide all available project list based on page and page size.Only agent-admin, agent-manager, agent-employee, agent-affiliate can use
   * @param {number} page
   * @param {number} pageSize
   * @return {Observable}
   */
  // 返回所有对用户可见的项目的摘要信息
  getAllSimpleProjectsByUser(page?, pageSize?) {
    return this.http.get(`${this.projectUrl}/auth/developer/projects/simple/by-user`);
  }

  /**
   * @function searchProjects
   * @description The function provide all projects list based on company, name or postcode
   * @param {number} companyId
   * @param {string} nameOrPostCode
   * @return {Observable}
   */
  // 在该用户可见的范围内查找, dev-admin调用
  searchProjects(companyId, nameOrPostCode?, page?, pageSize?) {
    const params = new HttpParams()
      .set('page', page)
      .set('pageSize', pageSize);

    if (nameOrPostCode) {
      return this.http.get(`${this.projectUrl}/auth/developer/projects/simple/by-develop-company-name-postcode/${companyId}?nameOrPostcode=${nameOrPostCode}`, {params});
    } else {
      return this.http.get(`${this.projectUrl}/auth/developer/projects/simple/by-develop-company-name-postcode/${companyId}`, {params});
    }
  }

  /**
   * @function searchProjectsByUser
   * @description The function find all available project list which name contains {nameOrPostCode}
   * @param {nameOrPostCode}
   * @return {Observable}
   */
  // 在该用户可见的范围内查找, dev-manager/dev-employee调用
  searchProjectsByUser(nameOrPostCode?, page?, pageSize?) {
    if (nameOrPostCode) {
      return this.http.get(`${this.projectUrl}/auth/projects/simple/by-user-name-postcode?nameOrPostCode=${nameOrPostCode}`);
    } else {
      return this.http.get(`${this.projectUrl}/auth/projects/simple/by-user-name-postcode`);
    }
  }

  /**
   * @function editUserByAgentAdmin
   * @description The function allow agent-admin to edit user information
   * @param {FormData} fd
   * @return {Observable}
   */
  // dev admin修改用户
  editUserByDevAdmin(fd) {
    return this.http.put(`${this.systemUrl}/auth/users/by-dev`, fd);
  }

  /**
   * @function editUser
   * @description The function allow user to edit their own detail information
   * @param {FormData} fd
   * @return {Observable}
   */
  // 用户修改自己的资料
  editUser(fd) {
    return this.http.put(`${this.systemUrl}/auth/users/by-auth`, fd);
  }

  /**
   * @function createProject
   * @description The function allow dev-admin to create a new project
   * @param {FormData} fd
   * @return {Observable}
   */
  // 创建项目
  createProject(fd: FormData) {
    return this.http.post(`${this.projectUrl}/auth/developer/projects`, fd);
  }

  /**
   * @author Damon Gao
   * @description Save draft project
   * @param fd
   */
  saveDraftProject(fd: FormData) {
    return this.http.post(`${this.projectUrl}/auth/developer/projects`, fd);
  }

  /**
   * @author Damon Gao
   * @description Update draft project
   * @param fd
   * @param id
   */
  updateDraftProject(fd: FormData, id: string) {
    return this.http.put(`${this.projectUrl}/auth/developer/projects/${id}`, fd);
  }

  /**
   * @author Damon Gao
   * @description Delete Draft Project
   * @param id
   */
  deleteDraftProject(id: string) {
    return this.http.delete(`${this.projectUrl}/auth/developer/projects/${id}`);
  }

  /**
   * @function updateProject
   * @description The function allow dev-admin, dev-manager to update project
   * @param {FormData} fd
   * @param {string} projectId
   * @return {Observable}
   */
  // 修改项目基本信息
  updateProject(fd: FormData, projectId) {
    return this.http.put(`${this.projectUrl}/auth/developer/projects/${projectId}`, fd);
  }

  /**
   * @function deleteProject
   * @description The function allow dev-admin to delete project by project id
   * @param {string} projectId
   * @return {Observable}
   */
  // 删除项目
  deleteProject(projectId) {
    return this.http.delete(`${this.projectUrl}/auth/developer/projects/${projectId}`);
  }

  /**
   * @function getAllTagsByUser
   * @description The function search all tag by status and scope
   * @param None
   * @return {Observable}
   */
  //  用户查询tags;
  getAllTagsByUser() {
    // page,pageSize选填
    return this.http.get(`${this.systemUrl}/anon/tags/by-user`);
  }

  /**
   * @function uploadTermsFiles
   * @description The function allow dev-admin, dev-manager to create project's term files
   * @param {string} projectId
   * @param {FormData} fd
   * @return {Observable}
   */
  // 上传项目条款文件
  uploadTermsFiles(projectId, fd) {
    return this.http.put(`${this.projectUrl}/auth/developer/projects/term-files/${projectId}`, fd);
  }

  /**
   * @function uploadDocumentFiles
   * @description The function allow dev-admin, dev-manager to create project's document files
   * @param {string} projectId
   * @param {FormData} fd
   * @return {Observable}
   */
  // 上传项目描述文件
  uploadDocumentFiles(projectId, fd) {
    return this.http.put(`${this.projectUrl}/auth/developer/projects/document-files/${projectId}`, fd);
  }

  // 上传项目EOI文件
  uploadEoiFile(projectId, fd) {
    return this.http.put(`${this.projectUrl}/auth/developer/projects/eoi-file/${projectId}`, fd)
  }

  deleteEoiFile(projectId) {
    return this.http.delete(`${this.projectUrl}/auth/developer/projects/eoi-file/${projectId}`)
  }

  /**
   * @function deleteTermsFiles
   * @description The function allow dev-admin, dev-manager to delete project's term file
   * @param {string} projectId
   * @param {string} fileIds
   */
  // 删除项目条款文件
  deleteTermsFiles(projectId, fileIds) {
    return this.http.delete(`${this.projectUrl}/auth/developer/projects/term-files/${projectId}?termFileIds=${fileIds}`);
  }

  /**
   * @function deleteDocumentFiles
   * @description The function allow dev-admin, dev-manager to delete project's document files
   * @param {string} projectId
   * @param {string} fileIds
   * @return {Observable}
   */
  // 删除项目描述文件
  deleteDocumentFiles(projectId, fileIds) {
    return this.http.delete(`${this.projectUrl}/auth/developer/projects/document-files/${projectId}?documentFileIds=${fileIds}`);
  }


  /**
   * @function uploadCompanyLogo
   * @description The function allow dev-admin to upload company logo by updating company detail
   * @param {FormData} fd
   * @param {string} companyId
   * @return {Observable}
   */
  // 上传公司logo
  uploadCompanyLogo(fd, companyId) {
    return this.http.put(`${this.systemUrl}/auth/companies/by-dev/${companyId}`, fd);
  }

  /**
   * @function uploadCompanyBanner
   * @description The function allow dev-admin to upload company banner by updating company detail
   * @param {FormData} fd
   * @param {string} companyId
   * @return {Observable}
   */
  // 上传公司banner
  uploadCompanyBanner(fd, companyId) {
    return this.http.put(`${this.systemUrl}/auth/companies/by-dev/${companyId}`, fd);
  }

  /**
   * @function editCompanyInfo
   * @description The function allow dev-admin to update company detail
   * @param {FormData} fd
   * @param {string} companyId
   * @return {Observable}
   */
  // 修改公司信息
  editCompanyInfo(fd, companyId) {
    return this.http.put(`${this.systemUrl}/auth/companies/by-dev/${companyId}`, fd);
  }

  /**
   * @function uploadProjectLogo
   * @description The function allow dev-admin, dev-manager to create project's logo
   * @param {string} projectId
   * @param {FormData} fd
   * @return {Observable}
   */
  // 上传项目logo
  uploadProjectLogo(projectId, fd) {
    return this.http.put(`${this.projectUrl}/auth/developer/projects/logo/${projectId}`, fd);
  }

  /**
   * @function deleteProjectLogo
   * @description The function allow dev-admin, dev-manager to delete project's logo
   * @param {string} projectId
   * @return {Observable}
   */
  // 删除项目logo
  deleteProjectLogo(projectId) {
    return this.http.delete(`${this.projectUrl}/auth/developer/projects/logo/${projectId}`);
  }

  /**
   * @function uploadGalleryImage
   * @description The function allow dev-admin, dev-manager to upload project's images
   * @param {string} projectId
   * @param {FormData} fd
   * @return {Observable}
   */
  // 上传项目轮播图
  uploadGalleryImage(fd, projectId) {
    return this.http.put(`${this.projectUrl}/auth/developer/projects/images/${projectId}`, fd);
  }

  /**
   * @function editGalleryImage
   * @description The function allow dev-admin, dev-manager to edit project's images
   * @param {FormData} fd
   * @return {Observable}
   */
  // 修改项目轮播图
  editGalleryImage(fd) {
    return this.http.put(`${this.systemUrl}/auth/image/project/by-dev`, fd);
  }

  /**
   * @function editGalleryImage
   * @description The function allow dev-admin, dev-manager to delete project's images
   * @param {string} imageId
   * @param {string} projectId
   * @return {Observable}
   */
  // 删除项目轮播图
  deleteGalleryImage(imageId, projectId) {
    return this.http.delete(`${this.projectUrl}/auth/developer/projects/images/${projectId}?imageIds=${imageId}`);
  }

  /**
   * @function uploadFloorPlans
   * @description The function allow dev-admin, dev-manager upload floor plan
   * @param {FormData} fd
   * @return {Observable}
   */
  // 创建户型图
  uploadFloorPlans(fd) {
    return this.http.post(`${this.projectUrl}/auth/floor-plans/by-dev`, fd);
  }

  /**
   * @function getAllFloorPlansByProjectId
   * @description The function find all floor plan by project id
   * @param {string} projectId
   * @return {Observable}
   */
  // 查找一个project的所有户型图
  getAllFloorPlansByProjectId(projectId) {
    return this.http.get(`${this.projectUrl}/auth/floor-plans/project?projectId=${projectId}`);
  }

  /**
   * @function addImageToFloorPlan
   * @description The function allow dev-admin, dev-manager to create image in floor plan
   * @param {FormData} fd
   * @param {number} floorPlanId
   * @return {Observable}
   */
  // 在户型图中插入图片
  addImageToFloorPlan(fd, floorPlanId) {
    return this.http.put(`${this.projectUrl}/auth/floor-plans/images/by-dev?floorPlanId=${floorPlanId}`, fd);
  }

  /**
   * @function editFloorPlanNotes
   * @description The function allow dev-admin, dev-manager to edit floor plan
   * @param {FormData} fd
   * @param {number} floorPlanId
   * @return {Observable}
   */
  // 修改户型图notes
  editFloorPlanNotes(fd, floorPlanId) {
    return this.http.put(`${this.projectUrl}/auth/floor-plans/by-dev/${floorPlanId}`, fd);
  }

  /**
   * @function deleteImageFromFloorPlan
   * @description The function allow dev-admin, dev-manager to delete image from floor plan
   * @param {number} floorPlanId
   * @param {number} imageId
   * @return {Observable}
   */
  // 在户型图中删除图片
  deleteImageFromFloorPlan(floorPlanId, imageId) {
    // fd: floorPlanId, imageId
    return this.http.delete(`${this.projectUrl}/auth/floor-plans/images/by-dev/${floorPlanId}/${imageId}`);
  }

  /**
   * @function deleteFloorPlans
   * @description The function allow dev-admin, dev-manager to delete floor plans
   * @param {number} floorPlanId
   * @return {Observable}
   */
  // 删除户型图
  deleteFloorPlans(floorPlanId) {
    return this.http.delete(`${this.projectUrl}/auth/floor-plans/by-dev/${floorPlanId}`);
  }

  /**
   * @function getPropertyById
   * @description The function provides property detail by property id
   * @param {number} propertyId
   * @return {Observable}
   */
  // 根据propertyId查询property详细信息
  getPropertyById(propertyId) {
    return this.http.get(`${this.projectUrl}/auth/properties/${propertyId}`);
  }

  /**
   * @function createProperty
   * @description The function allow dev-admin, dev-manager to create property
   * @param {FormData} fd
   * @return {Observable}
   */
  // 创建properties
  createProperty(fd) {
    return this.http.post(`${this.projectUrl}/auth/properties/by-dev`, fd);
  }

  /**
   * @function updateProperty
   * @description The function allow dev-admin, dev-manager to update property detail
   * @param {number} propertyId
   * @param {FormData} fd
   * @return {Observable}
   */
  // 修改property
  updateProperty(propertyId, fd) {
    return this.http.put(`${this.projectUrl}/auth/properties/by-dev/${propertyId}`, fd);
  }

  /**
   * @function updatePropertyBatch
   * @description The function allow dev-admin, dev-manager to update property detail
   * @param {number} propertyIds
   * @param {FormData} fd
   * @return {Observable}
   */
  // 批量修改property
  updatePropertyBatch(fd) {
    return this.http.put(`${this.projectUrl}/auth/properties/batch/by-dev`, fd);
  }

  /**
   * @function deleteProperty
   * @description The function allow dev-admin, dev-manager to delete property
   * @param {number} propertyId
   * @return {Observable}
   */
  // 删除property
  deleteProperty(propertyId) {
    return this.http.delete(`${this.projectUrl}/auth/properties/by-dev/${propertyId}`);
  }

  /**
   * @function addPropertyDocument
   * @description The function allow dev-admin, dev-manager to create document in property
   * @param {number} propertyId
   * @param {FormData} fd
   * @return {Observable}
   */
  // 向property中添加document
  addPropertyDocument(propertyId, fd) {
    return this.http.put(`${this.projectUrl}/auth/properties/documents/by-dev`, fd);
  }

  /**
   * @function deletePropertyDocument
   * @description The function allow dev-admin, dev-manager to delete document in property
   * @param {number} propertyId
   * @param {Array<number>} documentFileIds
   * @return {Observable}
   */
  // 从property中删除document
  deletePropertyDocument(propertyId, documentFileIds) {
    return this.http.delete(`${this.projectUrl}/auth/properties/documents/by-dev/${propertyId}?documentFileIds=${documentFileIds}`);
  }

  /**
   * @function getAllAgent
   * @description The function provides all available agents
   * @param {number} page
   * @param {number} pageSize
   * @return {Observable}
   */
  getAllAgent(page, pageSize, key?) {
    if (key) {
      return this.http.get(`${this.systemUrl}/auth/users/agents?key=${key}&page=${page}&pageSize=${pageSize}`);
    } else {
      return this.http.get(`${this.systemUrl}/auth/users/agents?page=${page}&pageSize=${pageSize}`);

    }
  }

  /**
   * @function createBuyerInfo
   * @description The function can create a new buyer
   * @param {FormData} fd
   * @return {Observable}
   */
  createBuyerInfo(fd) {
    return this.http.post(`${this.consumerUrl}/auth/buyer`, fd);
  }

  /**
   * @function editBuyerInfo
   * @description The function can edit buyer info by dev-admin
   * @param {number} buyerId
   * @param {FormData} fd
   * @return {Observable}
   */
  editBuyerInfo(buyerId, fd) {
    return this.http.put(`${this.projectUrl}/auth/buyer/${buyerId}`, fd);
  }

  /**
   * @function unlinkBuyerInfoByDevAdmin
   * @description The function allow dev-admin to delete buyer info
   * @param {number} buyerId
   * @param {number} propertyId
   * @return {Observable}
   */
  unlinkBuyerInfoByDevAdmin(buyerId, propertyId) {
    return this.http.delete(`${this.projectUrl}/auth/buyer/by-dev/${buyerId}?propertyId=${propertyId}`);
  }

  /**
   * @function createBuyerChoice
   * @description The function allow buyer to find all buyer choice by dev-admin, admin
   * @param {FormData} fd
   * @return {Observable}
   */
  createBuyerChoice(fd) {
    return this.http.post(`${this.consumerUrl}/auth/buyer-choice`, fd);
  }

  /**
   * @function deleteBuyerChoiceByDevAdmin
   * @description The function allow dev-admin to delete buyer choice
   * @param {number} buyerChoiceId
   * @return {Observable}
   */
  deleteBuyerChoiceByDevAdmin(buyerChoiceId) {
    return this.http.delete(`${this.projectUrl}/auth/buyer-choice/by-dev/${buyerChoiceId}`);
  }

  /**
   * @function createLawyerInfo
   * @description The function allow user to create layer
   * @param {FormData} fd
   * @return {Observable}
   */
  createLawyerInfo(fd) {
    return this.http.post(`${this.projectUrl}/auth/lawyer`, fd);
  }

  /**
   * @function unlinkLawyerInfoByDevAdmin
   * @description The function allow dev-admin to delete lawyer
   * @param {number} lawyerId
   * @param {number} propertyId
   * @return {Observable}
   */
  unlinkLawyerInfoByDevAdmin(lawyerId, propertyId) {
    return this.http.delete(`${this.projectUrl}/auth/lawyer/by-dev/${lawyerId}?propertyId=${propertyId}`);
  }

  cognitoLogin(fd: FormData): Observable<any> {
    const httpOptions = {};
    return this.http.post(`${API_PUBLIC_URL}/yp-api/system/cognito/token`, fd, httpOptions);
  }

  /**
   * @function userLogout
   * @description The function allow user to log out of the system
   * @param None
   * @return {Observable}
   */
  userLogout(): Observable<APIResponse> {
    return this.http.post<APIResponse>(`${this.systemUrl}/anon/users/logout`, null);
  }

  /**
   * @function reLogin
   * @description The function allow user to relogin to the system
   * @param None
   * @return None
   */
  reLogin(): Observable<APIResponse> {
    return this.http.get<APIResponse>(`${this.systemUrl}/anon/users/re-login`);
  }

  /**
   * @function searchRoles
   * @description The function find all roles. Only can be used by admin
   * @param None
   * @return {Observable<APIResponse>>}
   */
  searchRoles(): Observable<APIResponse> {
    return this.http.get<APIResponse>(`${this.apiUrl}/auth/roles`);
  }

  /**
   * @function searchEntities
   * @description This API has not been found in API doc
   */
  searchEntities(entityName: string, entityId: number, page?: number, pageSize?: number) {
    let param: HttpParams;
    if (arguments.length === 4) {
      // page
      param = new HttpParams()
        .set('entityName', entityName)
        .set('entityId', entityId.toString())
        .set('page', page.toString())
        .set('pageSize', pageSize.toString());
    } else {
      param = new HttpParams().set('entityName', entityName).set('entityId', entityId.toString());
    }
    return this.http.get<APIResponse>(`${this.apiUrl}/auth/photos`, {params: param});
  }

  /**
   * @function createLot
   * @description The function allow dev-admin, dev-manager to create lot
   * @param {FormData} fd
   * @return {Observable}
   */
  createLot(fd) {
    return this.http.post(`${this.projectUrl}/auth/lots/by-dev`, fd);
  }

  /**
   * @function updateLot
   * @description The function allow dev-admin, dev-manager to update lot
   * @param {FormData} fd
   * @param {number} lotId
   * @return {Observable}
   */
  updateLot(fd, lotId) {
    return this.http.put(`${this.projectUrl}/auth/lots/by-dev/${lotId}`, fd);
  }

  /**
   * @function updateLotBatch
   * @description The function allow dev-admin, dev-manager to update property detail
   * @param {number} lotIds
   * @param {FormData} fd
   * @return {Observable}
   */
  // 批量修改lot
  updateLotBatch(fd) {
    return this.http.put(`${this.projectUrl}/auth/lots/batch/by-dev`, fd);
  }

  /**
   * @function deleteLot
   * @description The function allow developer tp delete lot
   * @param {number} lotId
   * @return None
   */
  deleteLot(lotId) {
    return this.http.delete(`${this.projectUrl}/auth/lots/by-dev/${lotId}`);
  }

  /**
   * @function getLotDetail
   * @description The function allow admin, dev-admin, dev-manager, dev-employee to show lot details by roles
   * @param {number} lotId
   * @return {Observable}
   */
  getLotDetail(lotId) {
    return this.http.get(`${this.projectUrl}/auth/lots/${lotId}`);
  }

  /**
   * @function getPackageByLot
   * @description The function allow user to find package by lot
   * @param None
   * @return {Observable}
   */
  getPackageByLot(lotId) {
    return this.http.get(`${this.projectUrl}/auth/lots/packages/${lotId}`);
  }

  /**
   * @function getSuburbPopulation
   * @description The function allow user to search population facilities of project detail
   * @param {string} postCode
   * @return {Observable}
   */
  getSuburbPopulation(postCode) {
    return this.http.get(`${this.projectUrl}/anon/map/population?postcode=${postCode}`);
  }

  /**
   * @function uploadArModel
   * @description The function allow dev-admin, dev-manager to create url file in floor plan
   * @param {number} floorPlanId
   * @param {FormData} fd
   * @param {string} prefix
   * @return {Observable}
   */
  // 为某个户型，上传对应的AR模型
  uploadArModel(floorPlanId, fd, prefix) {
    return this.http.put(`${this.projectUrl}/auth/floor-plans/files/by-dev/${prefix}?floorPlanId=${floorPlanId}`, fd, {
      reportProgress: true,
      observe: 'events'
    });
  }

  /**
   * @function getNotificationByAuth
   * @description The function allow user to search all notifications
   * @param {string} status
   * @param {number} page
   * @param {number} pageSize
   * @return {Observable}
   */
  // 获取当前用户notification 数据
  getNotificationByAuth(status, page, pageSize) {
    return this.http.get(`${this.messageUrl}/auth/ntfy/by-auth?status=${status}&page=${page}&pageSize=${pageSize}`);
  }

  /**
   * @function putNotificationStatusByAuth
   * @description The function allow user to edit notification status to already read
   * @param {number} id
   * @return {Observable}
   */
  // 修改notification的状态为已读
  putNotificationStatusByAuth(id) {
    return this.http.put(`${this.messageUrl}/auth/ntfy/by-auth/${id}`, '');
  }

  /**
   * @function getUnreadNotification
   * @description The function provides the count of unread notification
   * @param None
   * @return {Observable}
   */
  // 获得未读notification的数量
  getUnreadNotification() {
    return this.http.get(`${this.messageUrl}/auth/ntfy/count`);
  }

  /**
   * @function deleteIosAr
   * @desc delete ios ar of a floorplan
   * @param {number} floorPlanId
   * @return {Observable}
   */
  deleteIosAr(floorPlanId) {
    return this.http.delete(`${this.projectUrl}/auth/floor-plans/files/by-dev/${floorPlanId}/ios`);
  }

  /**
   * @function deleteAndroidAr
   * @desc delete android ar of a floorplan
   * @param {number} floorPlanId
   * @return {Observable}
   */
  deleteAndroidAr(floorPlanId) {
    return this.http.delete(`${this.projectUrl}/auth/floor-plans/files/by-dev/${floorPlanId}/android`);
  }

  getSales(projectId, page?, pageSize?, name?) {
    return this.http.get(`${this.projectUrl}/auth/developer/projects/apply-user/list?projectId=${projectId}&page=${page}&pageSize=${pageSize}&name=${name}`);
  }

  updateSalesOfProjectById(id, fd) {
    return this.http.put(`${this.projectUrl}/auth/developer/projects/${id}`, fd);
  }

  getAgentCompanyList(page, pageSize, name) {
    return this.http.get(`${this.systemUrl}/auth/companies/agency?page=${page}&pageSize=${pageSize}&name=${name}`);
  }

  // 获取中介列表
  getAgencyList(id) {
    return this.http.get(`${this.projectUrl}/projects/${id}/agencies`)
  }

  // 中介列表条件筛选
  searchAgencyList(id, name) {
    return this.http.get(`${this.projectUrl}/projects/${id}/agencies?name=${name}`)
  }

  // 获取绑定的中介
  getBindAgencyList(id) {
    return this.http.get(`${this.projectUrl}/properties/${id}/agencies`)
  }

  // 绑定中介
  confirmAgency(id, fd) {
    return this.http.put(`${this.projectUrl}/projects/${id}/agencies/batch`, fd)
  }

  // property内绑定中介
  confirmAgencyAlone(id, fd) {
    return this.http.put(`${this.projectUrl}/projects/${id}/agencies`, fd)
  }

  // 获取project下Agency标签页数据
  getProjectAgency(id, params) {
    return this.http.get(`${this.projectUrl}/projects/${id}/paged-agencies`, {params: params})
  }

  // 获取项目申请中的中介分页列表
  pendingProjectAgencies(id, params) {
    return this.http.get(`${this.projectUrl}/projects/${id}/paged-agencies/pending`, {params: params})
  }

  // 获取项目申请Log
  pagedListProjectApplicationLogs(projectId, params) {
    return this.http.get(`${this.projectUrl}/projects/${projectId}/application-logs`, {params: params})
  }

  // 修改project下Agency的标签页各数据状态
  changeProjectAgencyStatus(type, projectId, agencyId) {
    return this.http.put(`${this.projectUrl}/projects/${projectId}/agencies/${agencyId}/status`, type)
  }

  // 获取project下Agnecy的Allocation
  getAgencyAllocation(projectId, agencyId) {
    return this.http.get(`${this.projectUrl}/projects/${projectId}/agencies/${agencyId}/properties`)
  }

  // 删除project下Agnecy的Allocation
  deleteAgencyAllocation(agencyId, propertyId) {
    return this.http.delete(`${this.projectUrl}/properties/${propertyId}/agencies/${agencyId}`)
  }

  // 获取agency代理项目的佣金设置
  getProjectCommissionSetting(projectId, agencyId) {
    return this.http.get(`${this.agentUrl}/developer/projects/${projectId}/agencies/${agencyId}/commission-setting`)
  }

  // 佣金设置
  modifyCommissionStageSetting(projectId, agencyId, fd) {
    return this.http.post(`${this.agentUrl}/developer/projects/${projectId}/agencies/${agencyId}/commission-setting`, fd)
  }

  // Pending状态的佣金设置(通过)
  approveProjectApply(applyId, fd) {
    return this.http.put(`${this.projectUrl}/project-applications/${applyId}/application/approve`, fd)
  }

  // 拒绝
  rejectProjectApply(applyId, fd) {
    return this.http.put(`${this.projectUrl}/project-applications/${applyId}/application/reject`, fd)
  }

  //变更EOI状态
  changeProjectAgencyEOIState(projectId, agencyId, fd) {
    return this.http.put(`${this.projectUrl}/projects/${projectId}/agencies/${agencyId}/eoi/status`, fd)
  }

  //变更项目是否需要审核
  changeProjectNeedApproval(projectId, fd) {
    return this.http.put(`${this.projectUrl}/developer/projects/${projectId}/approve-status`, fd)
  }

  // 新增EOI
  addEoi(projectId, formData) {
    return this.http.post(`${this.projectUrl}/projects/${projectId}/eois`, formData);
  }

  // 获取EOI list
  pagedListEois(projectId, page, pageSize, fd) {
    return this.http.get(`${this.projectUrl}/projects/${projectId}/eois?page=${page}&pageSize=${pageSize}`, {params: fd})
  }

  // 获取EOI模板
  getEoiForm(projectId) {
    return this.http.get(`${this.projectUrl}/developer/projects/${projectId}/eoi-form`)
  }

  // 获取EOI详情
  getEoiDetail(id) {
    return this.http.get(`${this.projectUrl}/eois/${id}`);
  }

  // 通过EOI申请
  approvedEoi(id) {
    return this.http.put(`${this.projectUrl}/eois/${id}/approve`, null);
  }

  // 拒绝EOI申请
  rejectEoi(id, fd) {
    return this.http.put(`${this.projectUrl}/eois/${id}/reject`, fd);
  }

  // 移除EOI申请
  removeEoi(id, fd) {
    return this.http.put(`${this.projectUrl}/eois/eois/${id}/remove`, fd);
  }

  //查询日志记录详情
  getApproveLogDetail(logId) {
    return this.http.get(`${this.projectUrl}/project-apply-logs/${logId}`)
  }

  getApplyDetail(id) {
    return this.http.get(`${this.projectUrl}/project-applications/${id}`)
  }

  getInfo() {
    return this.http.get(`${this.systemUrl}/auth/users/getInfo`);
  }

  saveEmail(fd) {
    return this.http.post(`${this.systemUrl}/anon/collection/saveEmail`, fd);
  }

  /**
   * @author Damon Gao
   * @description AI Generator Project Description
   * @param data
   */
  createProjectDescription(data: ProjectDescription): Observable<any> {
    return this.http.post(`${API_URL}/ai-market-hub/project-description/generator?apikey=${API_AI_KEY}`, data);
  }

  /**
   * @author Damon Gao
   * @description sign up
   * @param fd
   */
  signUp(fd): Observable<any> {
    return this.http.post(`${API_PUBLIC_URL}/yp-api/system/api/anon/users/create/without-code `, fd);
  }

  /**
   * @author Damon Gao
   * @function emailVerification
   * @description The function allow user to verify email
   * @param fd
   */
  emailVerification(fd: FormData): Observable<any> {
    return this.http.post(`${API_PUBLIC_URL}/yp-api/system/api/anon/users/mail/auth `, fd);
  }

  /**
   * @author Damon Gao
   * @function resendEmail
   * @description The function allow user to resend email
   * @param fd
   */
  resendEmail(fd: FormData): Observable<any> {
    return this.http.post(`${API_PUBLIC_URL}/yp-api/system/api/anon/users/mail/pre-auth/without-code`, fd);
  }
}
